import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Box, Button, Chip, IconButton, Stack, TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { AppBulkEditAction, AppPillToggle, secondaryButtonSx } from '@components'
import { MB_CHART_COLORS } from 'Pages/MyBusiness/_shared/Ui/scope'
import { alpha } from '@mui/material/styles'
import { DragIndicator as DragIndicatorIcon, AddCircleOutline as AddIcon, AddBoxOutlined as AddGroupIcon, Close as CloseIcon, EditOutlined as EditIcon, InboxOutlined as InboxIcon, TuneOutlined as SettingsIcon, Done as DoneIcon, Layers as LayersIcon, AutoAwesome as SuggestIcon } from 'Pages/DsRepricing/@components/icons'

import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { loadKey, saveKey, uid } from 'Pages/DsRepricing/Setup/@mocks'
import { useSetupCollection } from 'Pages/DsRepricing/Setup/@services'
import {
	Surface, SurfaceHeader, HelpNote, LoadingState,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import SuggestOrderDialog from 'Pages/DsRepricing/Setup/CategoryConsistency/SuggestOrder'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'

/**
 * Category Consistency — v3 "groups as columns" (Kanban).
 *
 * The unit of operation is the SIPP (ACRISS). Rather than a single
 * global ladder, the operator builds independent GROUPS. Each group
 * has its own ordered list of SIPPs and only competes within itself.
 *
 * Layout:
 *   ┌── Source ──┐ ┌── Group 1 ─┐ ┌── Group 2 ─┐ [+ Add group]
 *   │ Mini       │ │ MDMR       │ │ CDMR       │
 *   │  MBMR      │ │ EDMR       │ │ CDAR       │
 *   │  MDMR      │ │ CCMR       │ │            │
 *   │ Economy    │ └────────────┘ └────────────┘
 *   │  ...       │
 *   └────────────┘
 *
 * Rules:
 *   - Source lists every SIPP from the catalog, headed by its macro
 *     (macros are ONLY visual; the operational unit is the SIPP).
 *   - A SIPP lives in AT MOST ONE group (Curro, 2026-09-29 — "un
 *     acriss, un grupo, y cero líos"). It used to be allowed in many
 *     and the engine reconciles them, but two groups ordering the same
 *     pair the opposite way settle, silently, on both prices being
 *     equal. Dropping a SIPP that is already somewhere MOVES it; the
 *     server refuses the overlap too (`consistency_item_elsewhere`).
 *   - Drag a SIPP from Source to any group column to add it there.
 *   - Drag inside a group to reorder. Drag between groups to move/copy.
 *   - "×" on a SIPP chip removes it from that group only.
 */

// Persistence: see @services/useSetupCollection (entity 'consistency').
// The legacy localStorage migrations (v1→v4) were dropped on 2026-09-17:
// nothing in the mock storage was worth importing (Curro, brief Duda 1).

// One drag namespace per mode so a SIPP-drag never lands in a category
// column (they render in the same DOM tree when the user toggles quickly).
const DRAG_TYPE_SIPP     = 'application/x-dsrepricing-sipp'
const DRAG_TYPE_CATEGORY = 'application/x-dsrepricing-category'
const dragTypeFor = (mode) => mode === 'category' ? DRAG_TYPE_CATEGORY : DRAG_TYPE_SIPP

// Payload shape: `{code}` (from Source) or `{code, fromGroup}` (from a group).
function setDragPayload(e, mode, payload) {
	e.dataTransfer.effectAllowed = 'move'
	e.dataTransfer.setData(dragTypeFor(mode), JSON.stringify(payload))
	e.dataTransfer.setData('text/plain', payload.code)
}
function getDragPayload(e, mode) {
	const raw = e.dataTransfer.getData(dragTypeFor(mode))
	if (!raw) return null
	try { return JSON.parse(raw) } catch { return null }
}

// Flat row used by the collection hook: { id, mode, name, items }.
const MODE_KEY = 'catConsistencyMode'
function normaliseGroupRow(g) {
	return {
		id: g.id || uid(),
		mode: g.mode === 'category' ? 'category' : 'sipp',
		name: g.name || 'Group',
		items: Array.isArray(g.items) ? g.items : Array.isArray(g.sipps) ? g.sipps : [],
	}
}

export default function CategoryConsistencyTab() {
	// Groups are one flat collection with a `mode` each — the shape the
	// server stores. The two lattices are views over it; the active view
	// (`mode`) is a UI preference kept in localStorage, not data.
	const [allGroups, setAllGroups, sync] = useSetupCollection('consistency', {
		mockDefault: [], normalise: normaliseGroupRow,
	})
	const [mode, setModeState] = useState(() => loadKey(MODE_KEY, 'sipp') === 'category' ? 'category' : 'sipp')
	const [editingId, setEditingId] = useState(null)
	// The group whose order is being proposed, or null. One dialog for
	// the whole board: two proposals open at once would be two answers
	// to the same question.
	const [suggestingId, setSuggestingId] = useState(null)
	const [confirm, confirmDialog] = useConfirm()

	const groups = useMemo(() => allGroups.filter(g => g.mode === mode), [allGroups, mode])
	const setMode = (m) => { setModeState(m); saveKey(MODE_KEY, m) }
	const patchActive = (mut) => setAllGroups(prev => {
		const others = prev.filter(g => g.mode !== mode)
		const mine = mut(prev.filter(g => g.mode === mode)).map(g => ({ ...g, mode }))
		return [...others, ...mine]
	})

	// ------------------------------------------------------------------
	// Mutations (work on the currently active mode)
	// ------------------------------------------------------------------
	function addGroup() {
		patchActive(gs => [...gs, { id: uid(), name: `Group ${gs.length + 1}`, items: [] }])
	}
	function renameGroup(id, name) {
		patchActive(gs => gs.map(g => g.id === id ? { ...g, name } : g))
	}
	async function deleteGroup(id) {
		const group = groups.find(g => g.id === id)
		if (!await confirm({
			title: `Delete "${group?.name || 'this group'}"?`,
			body: 'The ordering is lost. The items themselves go back to the source pool and stay in any other group they belong to.',
			confirmLabel: 'Delete group',
			destructive: true,
		})) return
		patchActive(gs => gs.filter(g => g.id !== id))
	}
	// Every add is a move: the code leaves whichever group held it.
	// One value, one group — so there is no "copy" path any more, and
	// `fromGroup` on a drag payload is only used to tell a reorder
	// inside a group from a move between two.
	function addItemToGroup(groupId, code, atIndex = null) {
		patchActive(gs => gs.map(g => {
			if (g.id !== groupId) {
				return g.items.includes(code)
					? { ...g, items: g.items.filter(s => s !== code) }
					: g
			}
			const cur = g.items.filter(s => s !== code)
			if (atIndex == null || atIndex < 0 || atIndex > cur.length) cur.push(code)
			else cur.splice(atIndex, 0, code)
			return { ...g, items: cur }
		}))
	}
	// Used only by the order suggestion. `codes` is a permutation of the
	// group as the operator accepted it; anything the proposal did not
	// mention stays at the end rather than disappearing, because losing
	// a value to a reorder is the one outcome this feature must not have.
	function reorderGroup(groupId, codes) {
		patchActive(gs => gs.map(g => {
			if (g.id !== groupId) return g
			const wanted = codes.filter(c => g.items.includes(c))
			const rest = g.items.filter(c => !wanted.includes(c))
			return { ...g, items: [...wanted, ...rest] }
		}))
	}
	function removeItemFromGroup(groupId, code) {
		patchActive(gs => gs.map(g =>
			g.id === groupId ? { ...g, items: g.items.filter(s => s !== code) } : g,
		))
	}
	function moveItemToGroup(code, fromGroupId, toGroupId, atIndex = null) {
		if (fromGroupId === toGroupId) {
			return patchActive(gs => gs.map(g => {
				if (g.id !== toGroupId) return g
				const cur = g.items.filter(s => s !== code)
				const target = atIndex == null ? cur.length : atIndex
				cur.splice(Math.min(target, cur.length), 0, code)
				return { ...g, items: cur }
			}))
		}
		// Not only `fromGroupId`: a duplicate that already existed
		// elsewhere (from before the rule) must not survive the move.
		patchActive(gs => gs.map(g => {
			if (g.id === toGroupId) {
				const cur = g.items.filter(s => s !== code)
				const target = atIndex == null ? cur.length : atIndex
				cur.splice(Math.min(target, cur.length), 0, code)
				return { ...g, items: cur }
			}
			return g.items.includes(code)
				? { ...g, items: g.items.filter(s => s !== code) }
				: g
		}))
	}
	async function clearActive() {
		const kind = mode === 'sipp' ? 'ACRISS' : 'Category'
		const n = groups.length
		if (!await confirm({
			title: `Delete all ${n} ${kind} group${n === 1 ? '' : 's'}?`,
			body: `Every ordering built in the ${kind} view is lost.`,
			warning: 'This cannot be undone.',
			detail: n ? groups.map(g => g.name).join(' · ') : null,
			confirmLabel: `Delete ${n} group${n === 1 ? '' : 's'}`,
			destructive: true,
		})) return
		patchActive(() => [])
	}

	// ------------------------------------------------------------------
	// Derived: which groups already contain each code (for source badge)
	// ------------------------------------------------------------------
	const membership = useMemo(() => {
		const map = new Map()
		for (const g of groups) {
			for (const s of g.items) {
				if (!map.has(s)) map.set(s, [])
				map.get(s).push(g.id)
			}
		}
		return map
	}, [groups])

	return (
		<Surface padded={false}>
			{confirmDialog}
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Legend/>
						<Box>
						<strong>How the engine uses this:</strong> when a Strategy
						proposes a price change, the engine walks each group the ACRISS
						belongs to and checks that the ordering still holds. If MDMR
						(pos&nbsp;1 of "Compact ladders") would end up more expensive
						than EDMR (pos&nbsp;2 of the same group), the repricing is
						blocked and lands in the Activity log for review. ACRISS codes
						outside any group face no consistency check.
						</Box>
					</>}
					icon="layer-group"
					title="Category consistency"
					subtitle={mode === 'sipp' ? (
						<>Build <strong>groups</strong> of <strong>ACRISS codes</strong> that
						should stay ordered among themselves. Drag any ACRISS from the
						source pool into any group; the position inside the group IS
						the price ladder for that subset. An ACRISS can belong to any
						number of groups independently.</>
					) : (
						<>Build <strong>groups</strong> of <strong>categories</strong>{' '}
						(Mini, Economy, Compact…) that should stay ordered among
						themselves. Drag any category from the source pool into any
						group; the position inside the group IS the price ladder for
						that subset. Operates at the macro level — every ACRISS inside
						the category inherits the ordering.</>
					)}
					actions={
						<Stack direction="row" gap={1} alignItems="center">
							<ModeToggle mode={mode} onChange={setMode}/>
							<Button variant="outlined"
							        startIcon={<AddGroupIcon/>}
							        onClick={addGroup}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Add group
							</Button>
							<AppBulkEditAction danger startIcon={<CloseIcon/>} onClick={clearActive}>
								Clear all
							</AppBulkEditAction>
						</Stack>
					}
				/>


				{/* Two-pane layout: Source column (fixed 260) + horizontally
				    scrollable groups strip. Whole board scrolls together on
				    narrow viewports thanks to overflow-x on the wrapper. */}
				{sync.loading ? <LoadingState what="consistency groups"/> : (
				<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch',
				           overflowX: 'auto', pb: 1 }}>
					<SourceColumn mode={mode} membership={membership}
					              groupNames={new Map(groups.map(g => [g.id, g.name]))}/>

					{groups.map(group => (
						<GroupColumn key={group.id}
						             mode={mode}
						             group={group}
						             editing={editingId === group.id}
						             onStartEdit={() => setEditingId(group.id)}
						             onEndEdit={() => setEditingId(null)}
						             onRename={(name) => renameGroup(group.id, name)}
						             onDelete={() => deleteGroup(group.id)}
						             refused={sync.refused?.get(group.id)}
						             stalled={sync.stalled?.has(group.id)}
						             onSuggestOrder={() => setSuggestingId(group.id)}
						             onAddItem={(code, atIndex, fromGroup) =>
						               moveItemToGroup(code, fromGroup ?? null, group.id, atIndex)}
						             onRemoveItem={(code) => removeItemFromGroup(group.id, code)}
						             addItemAppend={(code, fromGroup) =>
						               (fromGroup ? moveItemToGroup(code, fromGroup, group.id)
						                          : addItemToGroup(group.id, code))}/>
					))}

					{/* Trailing "add group" tile at the end of the strip. */}
					<Box sx={{ minWidth: 200, display: 'flex' }}>
						<Button variant="outlined" fullWidth
						        startIcon={<AddGroupIcon/>}
						        onClick={addGroup}
						        sx={{ bgcolor: dsColorNew.white,
						              borderStyle: 'dashed',
						              borderColor: dsColorNew.textSubtle,
						              color: dsColorNew.textMuted,
						              minHeight: 200,
						              '&:hover': { bgcolor: dsColorNew.pillActiveBg,
						                           borderColor: dsColorNew.appBlue,
						                           color: dsColorNew.appBlue } }}>
							Add group
						</Button>
					</Box>
				</Box>
				)}

				{suggestingId && (
					<SuggestOrderDialog
						open
						mode={mode}
						items={groups.find(g => g.id === suggestingId)?.items ?? []}
						onApply={(codes) => {
							reorderGroup(suggestingId, codes)
							setSuggestingId(null)
						}}
						onClose={() => setSuggestingId(null)}/>
				)}

			</Box>
		</Surface>
	)
}

// ---------------------------------------------------------------------------
// Mode toggle (SIPP · Category)
// ---------------------------------------------------------------------------
const MODE_OPTIONS = [{ value: 'sipp', label: 'ACRISS' }, { value: 'category', label: 'Category' }]

function ModeToggle({ mode, onChange }) {
	return <AppPillToggle size="small" value={mode} options={MODE_OPTIONS} onChange={onChange} sx={{ mr: 1 }}
	                      title="Toggle the level at which you build hierarchies: individual ACRISS codes or ACRISS categories."/>
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------
function Legend() {
	return (
		// Read inside the card's ⓘ: one note per line
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: 13, color: dsColorNew.textDark }}>
			<span><strong style={{ color: dsColorNew.textTitle }}>Top of a group</strong> = cheapest ·{' '}
			<strong style={{ color: dsColorNew.textTitle }}>bottom</strong> = most expensive.</span>
			<span>Same ACRISS can live in <em>several</em> groups — each is
			evaluated independently.</span>
			<span>Macros in the source column are only for eye — the operational
			unit is the ACRISS.</span>
			{/* Named here because the icon alone was not enough: the feature
			    only pays off if the operator knows it exists before starting
			    to order by hand. */}
			<span><SuggestIcon sx={{ fontSize: 14, mb: '-2px',
			                         color: dsColorNew.appBlue }}/>{' '}
			on a group proposes an order from what your market charges —
			it shows its evidence and applies nothing until you accept.</span>
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Source column (all catalog SIPPs, grouped visually by macro)
// ---------------------------------------------------------------------------
function SourceColumn({ mode, membership, groupNames = new Map() }) {
	// The PMS's own catalogue, not a hand-written list. Eight codes the
	// engine reprices were missing from the mock and twenty it offered
	// do not exist in the fleet (2026-09-28).
	const { loading, items, tree, error } = useCategories()
	const nameOf = React.useMemo(
		() => new Map(items.map(c => [c.sipp, c])), [items])
	const totalCount = mode === 'category'
		? tree.length
		: tree.reduce((s, m) => s + m.sipps.length, 0)
	const label = mode === 'category' ? 'categories' : 'SIPPs'
	const unmapped = items.filter(c => !c.mapped).length
	return (
		<Box sx={{ ...columnSx, minWidth: 260, maxWidth: 260,
		           bgcolor: dsColorNew.tableRowAlt, borderStyle: 'dashed' }}>
			<Stack direction="row" gap={0.75} alignItems="center" sx={{ mb: 1 }}>
				<InboxIcon sx={{ fontSize: 18, color: dsColorNew.textMuted }}/>
				<Typography sx={columnTitleSx}>Source</Typography>
				<Box sx={{ flex: 1 }}/>
				<Typography sx={{ fontSize: 11, color: dsColorNew.textSubtle }}>
					{totalCount} {label}
				</Typography>
			</Stack>
			{error && (
				<Typography sx={{ fontSize: 11.5, color: dsColorNew.dangerRed, mb: 1 }}>
					{error}
				</Typography>
			)}
			{!loading && !error && unmapped > 0 && (
				/* Stored and never acted on: consistency is evaluated
				   against the ACRISS the rate shops return, and an
				   unmapped category has none. Said once, here, rather
				   than refusing the rule. */
				<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, mb: 1 }}>
					{unmapped} without a market mapping — usable in a rule, but the
					engine will not act on them until they are mapped.
				</Typography>
			)}
			<Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
				{loading ? (
					<Typography sx={{ fontSize: 11.5, color: dsColorNew.textSubtle }}>
						Loading the catalogue…
					</Typography>
				) : mode === 'sipp' ? (
					tree.map(macro => (
						<Box key={macro.code} sx={{ mb: 1 }}>
							<Typography sx={macroHeaderSx}>{macro.name}</Typography>
							<Stack gap={0.25}>
								{macro.sipps.map(sipp => (
									<SourceChipSipp key={sipp} code={sipp}
									                title={nameOf.get(sipp)?.name}
									                unmapped={nameOf.get(sipp)?.mapped === false}
									                usedInCount={membership.get(sipp)?.length || 0}
									                heldBy={groupNames.get(membership.get(sipp)?.[0])}/>
								))}
							</Stack>
						</Box>
					))
				) : (
					<Stack gap={0.35}>
						{tree.map(macro => (
							<SourceChipCategory key={macro.code} macro={macro}
							                    usedInCount={membership.get(macro.code)?.length || 0}/>
						))}
					</Stack>
				)}
			</Box>
		</Box>
	)
}

const macroHeaderSx = {
	fontSize: 11, fontWeight: 700,
	textTransform: 'uppercase', letterSpacing: '0.08em',
	color: dsColorNew.textMuted,
	pl: 0.5, mb: 0.5,
}

function SourceChipSipp({ code, usedInCount, title, unmapped = false, heldBy = null }) {
	const name = title || code
	const where = heldBy ? ` · in "${heldBy}" — dropping it elsewhere moves it` : ''
	return (
		<Box draggable
		     onDragStart={(e) => setDragPayload(e, 'sipp', { code })}
		     // The PMS's own name for the car. A four-letter code is not
		     // something anyone recognises, and the catalogue has the
		     // name sitting right next to it. And which group has it,
		     // because a drop now moves rather than copies.
		     title={(unmapped ? `${name} — no market mapping` : name) + where}
		     sx={sourceChipSx(code)}>
			<DragIndicatorIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle }}/>
			<Box component="code" sx={{ fontFamily: 'inherit',
			                            fontSize: 12, fontWeight: 700,
			                            color: dsColorNew.textTitle }}>
				{code}
			</Box>
			<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, flex: 1,
			                  overflow: 'hidden', textOverflow: 'ellipsis',
			                  whiteSpace: 'nowrap' }}>
				{title || ''}
			</Typography>
			{unmapped && (
				<Box sx={{ width: 6, height: 6, borderRadius: '50%',
				           bgcolor: dsColorNew.warningBar, flexShrink: 0 }}/>
			)}
			<UsedInBadge count={usedInCount}/>
		</Box>
	)
}

function SourceChipCategory({ macro, usedInCount }) {
	return (
		<Box draggable
		     onDragStart={(e) => setDragPayload(e, 'category', { code: macro.code })}
		     sx={{ ...sourceChipSx(macro.code), py: 0.6 }}>
			<DragIndicatorIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle }}/>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography sx={{ fontSize: 13, fontWeight: 700,
				                  color: dsColorNew.textTitle, lineHeight: 1.2 }}>
					{macro.name}
				</Typography>
				<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted,
				                  fontFamily: 'inherit' }}>
					{macro.sipps.length} SIPPs
				</Typography>
			</Box>
			<UsedInBadge count={usedInCount}/>
		</Box>
	)
}

function UsedInBadge({ count }) {
	if (!count) return null
	return (
		<Chip size="small"
		      icon={<LayersIcon sx={{ fontSize: 11, color: `${dsColorNew.appBlue} !important` }}/>}
		      label={count}
		      title={`Already in ${count} group${count === 1 ? '' : 's'}`}
		      sx={{ height: 16, fontSize: 11, fontWeight: 700,
		            bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
		            '& .MuiChip-label': { px: 0.5 },
		            '& .MuiChip-icon': { ml: 0.4, mr: -0.2 } }}/>
	)
}

const sourceChipSx = (code) => ({
	display: 'flex', alignItems: 'center', gap: 0.5,
	px: 0.75, py: 0.4,
	bgcolor: dsColorNew.white,
	border: '1px solid',
	borderColor: dsColorNew.grayBorder,
	borderLeft: '4px solid',
	borderLeftColor: colourForCode(code),
	borderRadius: 1,
	cursor: 'grab',
	'&:active': { cursor: 'grabbing' },
	'&:hover': { borderColor: dsColorNew.appBlue, bgcolor: dsColorNew.pillActiveBg },
	transition: 'border-color 100ms ease, background-color 100ms ease',
})

// ---------------------------------------------------------------------------
// Group column (user-defined subset)
// ---------------------------------------------------------------------------
function GroupColumn({ mode, group, editing, onStartEdit, onEndEdit, onRename,
                       onDelete, onSuggestOrder, onAddItem, onRemoveItem,
                       addItemAppend, refused, stalled }) {
	const [nameDraft, setNameDraft] = useState(group.name)
	const [dragOverColumn, setDragOverColumn] = useState(false)
	const inputRef = useRef(null)

	useEffect(() => setNameDraft(group.name), [group.name])
	useEffect(() => { if (editing) inputRef.current?.select() }, [editing])

	function commitRename() {
		const trimmed = nameDraft.trim() || group.name
		if (trimmed !== group.name) onRename(trimmed)
		onEndEdit()
	}

	function acceptDropAtEnd(e) {
		const p = getDragPayload(e, mode)
		setDragOverColumn(false)
		if (!p) return
		e.preventDefault()
		addItemAppend(p.code, p.fromGroup)
	}

	return (
		/* The whole column takes a drop, and it appends.
		 *
		 * Adding the second SIPP to a group meant hitting an
		 * eighteen-pixel strip below the last chip — everything else in
		 * the column was dead space, so an ordinary drag bounced
		 * (Curro, 2026-09-28). Precision should only be needed to say
		 * WHERE in the order it goes, not to get it in at all.
		 *
		 * The chips sit on top and stop the event, so dropping onto one
		 * still inserts before it. */
		<Box onDragOver={(e) => {
		         if (!e.dataTransfer.types.includes(dragTypeFor(mode))) return
		         e.preventDefault()
		         setDragOverColumn(true)
		     }}
		     onDragLeave={(e) => {
		         // Only when the pointer actually leaves the column, not
		         // when it crosses onto a chip inside it.
		         if (!e.currentTarget.contains(e.relatedTarget)) setDragOverColumn(false)
		     }}
		     onDrop={acceptDropAtEnd}
		     sx={{ ...columnSx, minWidth: 240, maxWidth: 240,
		           bgcolor: dragOverColumn ? dsColorNew.pillActiveBg : dsColorNew.white,
		           outline: dragOverColumn ? '2px dashed' : '2px dashed transparent',
		           outlineColor: dragOverColumn ? dsColorNew.appBlue : 'transparent',
		           outlineOffset: '-2px',
		           transition: 'background-color 120ms ease' }}>
			<Stack direction="row" gap={0.5} alignItems="center" sx={{ mb: 1 }}>
				{editing ? (
					<>
						<TextField inputRef={inputRef}
						           size="small" value={nameDraft}
						           onChange={(e) => setNameDraft(e.target.value)}
						           onKeyDown={(e) => {
						               if (e.key === 'Enter')  commitRename()
						               if (e.key === 'Escape') { setNameDraft(group.name); onEndEdit() }
						           }}
						           onBlur={commitRename}
						           inputProps={{ style: { fontSize: 13, fontWeight: 700,
						                                  padding: '4px 8px' } }}
						           sx={{ flex: 1, bgcolor: dsColorNew.tableRowAlt,
						                 '& .MuiOutlinedInput-root': { height: 28 } }}/>
						<IconButton size="small" onClick={commitRename}
						            sx={{ color: dsColorNew.appBlue, p: 0.5 }}>
							<DoneIcon fontSize="small"/>
						</IconButton>
					</>
				) : (
					<>
						<Typography sx={{ ...columnTitleSx, flex: 1, cursor: 'text',
						                  overflow: 'hidden', textOverflow: 'ellipsis',
						                  whiteSpace: 'nowrap' }}
						            onClick={onStartEdit}
						            title="Click to rename">
							{group.name}
						</Typography>
						<Chip size="small" label={group.items.length}
						      sx={{ height: 18, fontSize: 11, fontWeight: 700,
						            bgcolor: group.items.length === 0 ? dsColorNew.pillTrackBg : dsColorNew.pillActiveBg,
						            color:   group.items.length === 0 ? dsColorNew.textMuted : dsColorNew.appBlue }}/>
						{/* Only the server can hold a group back: this collection has
						    no `readyToSave` (CU-869ffwj7a). */}
						<UnsavedChip refused={refused} stalled={stalled} sx={{ mr: 0 }}/>
						{/* Proposes an order, never applies one: the dialog shows
						    what each position was based on and the operator decides.
						    Under two items there is nothing to order.

						    Coloured, and larger than rename and delete, on purpose.
						    It shipped as a third grey 14px icon between the other
						    two and Curro could not find it (2026-09-24) — which is
						    the whole feature, since an operator who does not see it
						    orders forty ACRISS by hand. Rename and delete are
						    housekeeping and stay quiet; this one is the offer. */}
						<IconButton size="small"
						            disabled={group.items.length < 2}
						            onClick={onSuggestOrder}
						            title={group.items.length < 2
						                ? 'Add at least two items to suggest an order'
						                : 'Suggest an order (cheapest first)'}
						            sx={{ color: dsColorNew.appBlue, p: 0.35,
						                  bgcolor: dsColorNew.pillActiveBg,
						                  '&.Mui-disabled': { color: dsColorNew.textSubtle,
						                                      bgcolor: 'transparent' },
						                  '&:hover': { bgcolor: dsColorNew.appBlue,
						                               color: dsColorNew.white } }}>
							<SuggestIcon sx={{ fontSize: 16 }}/>
						</IconButton>
						<IconButton size="small" onClick={onStartEdit}
						            title="Rename group"
						            sx={{ color: dsColorNew.textSubtle, p: 0.25,
						                  '&:hover': { color: dsColorNew.appBlue } }}>
							<EditIcon sx={{ fontSize: 14 }}/>
						</IconButton>
						<IconButton size="small" onClick={onDelete}
						            title="Delete group"
						            sx={{ color: dsColorNew.textSubtle, p: 0.25,
						                  '&:hover': { color: dsColorNew.dangerRed } }}>
							<CloseIcon sx={{ fontSize: 14 }}/>
						</IconButton>
					</>
				)}
			</Stack>

			{/* Items in this group */}
			<Box sx={{ flex: 1, overflowY: 'auto', pr: 0.25 }}>
				{group.items.length === 0 && (
					<EmptyDropTarget mode={mode} over={dragOverColumn}/>
				)}
				{group.items.map((code, idx) => (
					<GroupChip key={code}
					           mode={mode}
					           code={code}
					           position={idx + 1}
					           groupId={group.id}
					           onDropBefore={(payload) =>
					             onAddItem(payload.code, idx, payload.fromGroup ?? null)}
					           onRemove={() => onRemoveItem(code)}/>
				))}
				{/* Where an append will land. No handler of its own: the
				    column takes the drop, and a second one here would
				    bubble and append the same value twice. */}
				{group.items.length > 0 && (
					<Box sx={{
					         height: 18, mt: 0.5,
					         borderRadius: 0.5,
					         bgcolor: dragOverColumn ? dsColorNew.pillActiveBg : 'transparent',
					         borderTop: dragOverColumn ? '2px solid' : '2px dashed transparent',
					         borderTopColor: dragOverColumn ? dsColorNew.appBlue : 'transparent',
					     }}/>
				)}
			</Box>
		</Box>
	)
}

/** The empty state of a group. Purely a sign — the column is the target. */
function EmptyDropTarget({ mode, over = false }) {
	return (
		<Box sx={{
		         minHeight: 100,
		         display: 'flex', alignItems: 'center', justifyContent: 'center',
		         textAlign: 'center', p: 1.5,
		         bgcolor: over ? dsColorNew.noteInfoBg : dsColorNew.tableRowAlt,
		         border: '2px dashed',
		         borderColor: over ? dsColorNew.appBlue : dsColorNew.grayBorder,
		         borderRadius: 1,
		         color: dsColorNew.textSubtle, fontSize: 12, fontStyle: 'italic',
		     }}>
			Drag a {mode === 'category' ? 'category' : 'SIPP'} here from Source
		</Box>
	)
}

function GroupChip({ mode, code, position, groupId, onDropBefore, onRemove }) {
	const [over, setOver] = useState(false)
	const isCategory = mode === 'category'
	// Same catalogue the Source column reads; the hook caches per
	// mount, so a chip does not cost a request.
	const { tree } = useCategories()
	const macro = isCategory ? tree.find(m => m.code === code) : null
	const tx = !isCategory ? transmissionOf(code) : null
	const isM = tx === 'manual'
	return (
		<Box onDragOver={(e) => {
		         if (!e.dataTransfer.types.includes(dragTypeFor(mode))) return
		         e.preventDefault()
		         setOver(true)
		     }}
		     onDragLeave={() => setOver(false)}
		     onDrop={(e) => {
		         setOver(false)
		         const p = getDragPayload(e, mode)
		         if (!p) return
		         // The column behind this also accepts drops, and it
		         // appends. Without this the chip would insert and the
		         // column would append the same value.
		         e.stopPropagation()
		         if (p.code !== code) onDropBefore(p)
		     }}
		     sx={{
		         borderTop: over ? '2px solid' : '2px solid transparent',
		         borderTopColor: over ? dsColorNew.appBlue : 'transparent',
		     }}>
			<Box draggable
			     onDragStart={(e) => setDragPayload(e, mode, { code, fromGroup: groupId })}
			     sx={{
			         display: 'flex', alignItems: 'center', gap: 0.5,
			         px: 0.75, py: isCategory ? 0.6 : 0.4, mb: 0.25,
			         bgcolor: dsColorNew.white,
			         border: '1px solid',
			         borderColor: dsColorNew.grayBorder,
			         borderLeft: '4px solid',
			         borderLeftColor: colourForCode(code),
			         borderRadius: 1,
			         cursor: 'grab',
			         '&:active': { cursor: 'grabbing' },
			         '&:hover': { bgcolor: dsColorNew.tableRowAlt },
			     }}>
				<PositionBadge position={position} code={code}/>
				{isCategory ? (
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ fontSize: 12.5, fontWeight: 700,
						                  color: dsColorNew.textTitle, lineHeight: 1.2 }}>
							{macro?.name || code}
						</Typography>
						<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted,
						                  fontFamily: 'inherit' }}>
							{macro?.sipps.length || 0} SIPPs
						</Typography>
					</Box>
				) : (
					<>
						<Box component="code" sx={{ fontFamily: 'inherit',
						                            fontSize: 12, fontWeight: 700,
						                            color: dsColorNew.textTitle, flex: 1 }}>
							{code}
						</Box>
						<Chip size="small"
						      label={isM ? 'M' : 'A'}
						      title={isM ? 'Manual transmission' : 'Automatic transmission'}
						      sx={{
						          height: 18, minWidth: 18, fontWeight: 700, fontSize: 11,
						          bgcolor: isM ? dsColorNew.pillActiveBg : dsColorNew.noteAmberBg,
						          color:   isM ? dsColorNew.appBlue : dsColorNew.noteAmber,
						          '& .MuiChip-label': { px: 0 },
						      }}/>
					</>
				)}
				<IconButton size="small" onClick={onRemove}
				            title="Remove from this group"
				            sx={{ color: dsColorNew.textSubtle, p: 0.25,
				                  '&:hover': { color: dsColorNew.dangerRed } }}>
					<CloseIcon sx={{ fontSize: 14 }}/>
				</IconButton>
			</Box>
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Styles + helpers
// ---------------------------------------------------------------------------
const columnSx = {
	display: 'flex', flexDirection: 'column',
	border: '1px solid', borderColor: dsColorNew.grayBorder,
	borderRadius: 1,
	p: 1,
	maxHeight: 620,
	flex: '0 0 auto',
}
const columnTitleSx = {
	fontSize: 12, fontWeight: 700,
	textTransform: 'uppercase', letterSpacing: '0.04em',
	color: dsColorNew.textTitle,
}

const isManual    = (s) => 'MN'.includes(s[2])
const isAutomatic = (s) => 'ABCD'.includes(s[2])
const transmissionOf = (s) => isManual(s) ? 'manual' : isAutomatic(s) ? 'automatic' : 'other'

const IDENTITY_PALETTE = [
	// The app's data colours (the series colours of the charts, `MB_CHART_COLORS`)
	...MB_CHART_COLORS,
]
function colourForCode(code) {
	if (!code) return dsColorNew.textSubtle
	let hash = 0
	for (let i = 0; i < code.length; i++) hash = (hash * 31 + code.charCodeAt(i)) >>> 0
	return IDENTITY_PALETTE[hash % IDENTITY_PALETTE.length]
}

function PositionBadge({ position, code }) {
	const bg = colourForCode(code)
	return (
		<Box sx={{
			width: 22, height: 22,
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			bgcolor: bg, color: dsColorNew.white,
			borderRadius: '50%',
			fontWeight: 700, fontSize: 11,
			flexShrink: 0,
			boxShadow: `0 1px 2px ${alpha(dsColorNew.navy, 0.15)}`,
		}}>
			{position}
		</Box>
	)
}
